// 고장 조치 이력 — 로봇 이슈 / 로봇 외 이슈 자동 분류 정본(2026-10-27 오너 "키워드같은거로 자동 분류").
//
// 기준은 노션 'Field Issue Tracking' 의 Sort 칸에 사람이 붙여 온 값이다. 실제로 그렇게 나뉘어 있다:
//   로봇 외 이슈 — 그릴(와이어 그릴 · 갈비 그릴 · 연결핀) · 토글 · 불판 · 태블릿 충전 · 케이블 · 조리 퀄리티 ·
//                  사용 실수(상부 전원 안 켬 · 비상정지 눌림)
//   로봇 이슈    — 로더 · 와이어 풀리 · 모터 · RPI · 스토퍼 · 브레이크 · 앱 버그 · 업데이트 오류 …
// 액세서리(토글 · 그릴 · 태블릿 충전)는 자료마다 갈렸다(9월 하반기 리포트는 '태블릿 충전 안 됨'을 로봇 이슈로 셈).
// 오너 결정(2026-10-27): "기본은 로봇 외 이슈로 하고, 아닌경우엔 수동으로 바꿀게" — 그래서 로봇 외 이슈로 둔다.
// 화면은 사람이 고른 값이 있으면 그것을, 없으면 이 함수의 답을 쓴다(자동이라고 표시한다).
import { IssueCategory } from './enums.js';
/**
 * 사용 실수 · 설치 환경 — **원인**에 적히는 말이라 제목보다 먼저 본다.
 * 예: 제목 '로봇 동작 안함' + 원인 '로봇 상부 전원 안킴' → 로봇 외 이슈.
 */
const USAGE_RULES = [
    // '안 켰다'(사람)만 — '안 켜진다'(고장)는 아니다.
    [/전원\s*(을\s*)?(안|않)\s*(킴|켬|켰|켠)/, IssueCategory.NON_ROBOT],
    // '눌려 있었다'만 — '눌려있지 않고'(확인했다는 말)는 아니다.
    [/비상\s*정지\s*(버튼)?\s*(이\s*|가\s*)?눌려\s*(있었|있음|있어서|있던)/, IssueCategory.NON_ROBOT],
    [/동작법\s*미숙|오조작|사용\s*실수|사용자\s*실수|직원\s*실수|부주의/, IssueCategory.NON_ROBOT],
    // 로봇 운영 이슈 리포트(2026-09 하반기) — '태블릿 IP 잘못 입력'은 사용 실수로 로봇 외 이슈.
    [/ip\s*(를\s*)?(잘못|오입력|틀)/i, IssueCategory.NON_ROBOT],
    [/콘센트|접지|멀티탭|문어발/, IssueCategory.NON_ROBOT],
];
/** 위에서부터 처음 맞는 것. 순서가 뜻이다 — '와이어 그릴'(로봇 외)을 '와이어'(로봇)보다 먼저 본다. */
const RULES = [
    // 로봇이 움직이다 불판 · 그릴에 닿는 것은 로봇 동작 문제다(불판 · 그릴 낱말보다 먼저).
    [/로테이트|rotate|컨택|contact|틸트|tilt|불판에\s*(닿|걸|충돌)/i, IssueCategory.ROBOT],
    [/와이어\s*그릴|그릴\s*와이어|갈비\s*그릴/, IssueCategory.NON_ROBOT],
    [/그릴\s*(이\s*)?(벌어|분리|연결|결착|핀|고정|파손|처짐|늘어|끊)/, IssueCategory.NON_ROBOT],
    [/토글/, IssueCategory.NON_ROBOT],
    [/불판|코팅|가스\s*게이지|후드/, IssueCategory.NON_ROBOT],
    [/퀄리티|그을림|타는\s*현상/, IssueCategory.NON_ROBOT],
    [
        /태블릿[^,.\n]*(충전|파손|과열|깨짐|떨어|고장)|충전|케이블\s*(고장|불량|이슈|에러)/,
        IssueCategory.NON_ROBOT,
    ],
    [
        /로더|모터|와이어|풀리|rpi|라즈베리|\bpc\b|패널|led|스위치|스토퍼|브레이크|플립|그리퍼|체결|카메라|\bcam\b|센싱|불\s*감지|캘리브|앱|어플|업데이트|ota|통신|노란불|노란색|빨간불|온도\s*센서|예열|제퍼|사이드\s*팬|프레임|넘어|부팅|전원|와이파이|wi-?fi|offset|설정값|메뉴/i,
        IssueCategory.ROBOT,
    ],
];
function firstHit(rules, text) {
    for (const [re, cat] of rules)
        if (re.test(text))
            return cat;
    return null;
}
/**
 * 증상 · 조치 글로 구분을 정한다. 근거가 하나도 없으면 로봇 이슈(조치 이력 대부분이 로봇 고장이다).
 *
 * `symptom` 의 첫 줄이 제목 구실을 한다(노션 Issue Description). 제목에서 먼저 찾고, 없으면 나머지 글 전체에서 찾는다.
 */
export function classifyIssueCategory(input) {
    const symptom = input.symptom ?? '';
    const all = `${symptom}\n${input.action ?? ''}`;
    const title = symptom.split('\n')[0] ?? '';
    return (firstHit(USAGE_RULES, all) ??
        firstHit(RULES, title) ??
        firstHit(RULES, all) ??
        IssueCategory.ROBOT);
}
/** 노션 Sort 값 → 구분. 모르는 값이면 null(자동 분류로 넘긴다). */
export function issueCategoryFromNotionSort(sort) {
    if (sort === '로봇 이슈')
        return IssueCategory.ROBOT;
    if (sort === '로봇 외 이슈')
        return IssueCategory.NON_ROBOT;
    return null;
}
/**
 * 로봇 모델 칸(자유 입력) → 대시보드에서 묶는 이름. 노션은 'Gen1 · Gen2 · Zhepyr', 시트는 'GEN.2(M12)' 처럼 적는다.
 */
export function issueModelGroup(model) {
    const m = (model ?? '').toLowerCase();
    if (/gen\.?\s*-?1|1세대/.test(m))
        return 'GEN1';
    if (/gen\.?\s*-?2|2세대/.test(m))
        return 'GEN2';
    if (/zep|zhep|제퍼/.test(m))
        return 'ZEPHYR';
    return '기타';
}
//# sourceMappingURL=issue-category.js.map